import { useMsal } from '@azure/msal-react';
import { permissions } from '@src/entities/User/includes/permissions';
import { config } from '@uhg-abyss/web/tools/config';
import { merge } from 'lodash';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

/**
 * Default user context values for local and non-local environments.
 *
 * @typedef {Object} DefaultValues
 * @property {string} currentRole - The user's current role.
 * @property {string} displayName - The user's display name.
 * @property {string} emailAddress - The user's email address.
 * @property {boolean|null} hasAccess - Whether the user has access.
 * @property {Function} hasPermission - Function to check user permissions.
 * @property {boolean|null} hasRoles - Whether the user has roles.
 * @property {string} homeAccountId - Home account ID.
 * @property {string} localAccountId - Local account ID.
 * @property {Object} msalAccount - MSAL account object.
 * @property {string} msid - MSID identifier.
 * @property {string[]} roles - List of user roles.
 * @property {string} tenantId - Tenant ID.
 */
export const defaultValues = {
  currentRole: config('APP_ENV') === 'local' ? 'Admin' : '',
  displayName: config('APP_ENV') === 'local' ? 'Local User' : '',
  emailAddress: config('APP_ENV') === 'local' ? 'user@email.local' : '',
  hasAccess: config('APP_ENV') === 'local' ? true : null,
  hasPermission: () => {},
  hasRoles: config('APP_ENV') === 'local' ? true : null,
  homeAccountId: '',
  localAccountId: '',
  msalAccount: {},
  msid: config('APP_ENV') === 'local' ? 'userMsid123' : '',
  roles: config('APP_ENV') === 'local' ? [config('AUTHORIZATION_ROLE'), 'Role.Admin'] : [],
  tenantId: '',
};

/**
 * React context for user data.
 *
 * @type {React.Context<DefaultValues>}
 */
const UserContext = createContext(defaultValues);

/**
 * Custom hook to access the user context.
 *
 * @returns {DefaultValues} The current user context.
 */
export const useUserContext = () => {
  return useContext(UserContext);
};

/**
 * UserProvider component.
 *
 * Provides user context to child components.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components.
 * @returns {React.Element}
 */
export function UserProvider({ children }) {
  const { instance } = useMsal();

  /**
   * hasAccess
   *
   * Checks if the user has access to the application.
   *
   * @type {boolean}
   */
  const hasAccess = useMemo(() => {
    let theAccess = defaultValues.hasAccess;

    if (config('APP_ENV') !== 'local') {
      const activeAccount = instance.getActiveAccount();

      if (activeAccount && activeAccount.idTokenClaims && activeAccount.idTokenClaims.roles) {
        theAccess = activeAccount.idTokenClaims.roles.includes(config('AUTHORIZATION_ROLE'));
      } else {
        theAccess = false;
      }
    }

    return theAccess;
  }, [instance]);

  /**
   * roles
   *
   * Gets the current roles assigned to the authenticated user.
   *
   * @type {string[]}
   */
  const roles = useMemo(() => {
    let theRoles = defaultValues.roles;
    if (config('APP_ENV') !== 'local') {
      const activeAccount = instance.getActiveAccount();
      theRoles = activeAccount?.idTokenClaims?.roles || [];
    }
    return theRoles;
  }, [instance]);

  /**
   * hasRoles
   *
   * Checks if the authenticated user has roles assigned.
   *
   * @type {boolean}
   */
  const hasRoles = useMemo(() => {
    return roles.length > 1;
  }, [roles]);

  /**
   * account
   *
   * Synchronizes the account from the identity provider with the user context.
   *
   * @type {Object}
   */
  const account = useMemo(() => {
    const theAccount = {
      displayName: defaultValues.displayName,
      emailAddress: defaultValues.emailAddress,
      homeAccountId: defaultValues.homeAccountId,
      localAccountId: defaultValues.localAccountId,
      msalAccount: defaultValues.msalAccount,
      msid: defaultValues.msid,
      tenantId: defaultValues.tenantId,
    };

    if (config('APP_ENV') !== 'local') {
      const activeAccount = instance.getActiveAccount();
      theAccount.displayName = activeAccount?.idTokenClaims?.name;
      theAccount.emailAddress = activeAccount?.idTokenClaims?.email;
      theAccount.homeAccountId = activeAccount?.homeAccountId;
      theAccount.localAccountId = activeAccount?.localAccountId;
      theAccount.msalAccount = activeAccount;
      theAccount.msid = activeAccount?.idTokenClaims?.msid;
      theAccount.tenantId = activeAccount?.tenantId;
    }

    return theAccount;
  }, [instance]);

  /**
   * currentRole
   *
   * Determines the current role of the user based on permissions.
   *
   * @type {string}
   */
  const currentRole = useMemo(() => {
    const permissionRoles = Object.keys(permissions);

    const matchingRoles = permissionRoles.filter((permissionRole) => {
      return roles.includes(`Role.${permissionRole}`);
    });

    return matchingRoles?.[0];
  }, [roles, permissions]);

  /**
   * hasPermission
   *
   * Checks if the user has a specific permission for a resource and attribute.
   *
   * @param {string|null} resource - Resource name.
   * @param {string|null} attribute - Attribute name.
   * @returns {boolean} True if the user has permission, false otherwise.
   */
  const hasPermission = useCallback(
    (resource = null, attribute = null) => {
      const thePermissions = { ...permissions?.[currentRole] };

      if (hasRoles) {
        const addOnRoles = [config('AUTHORIZATION_ROLE'), config('IRE_ROLE'), config('PII_ROLE')];

        addOnRoles.forEach((role) => {
          if (typeof role === 'string' && role.includes('.')) {
            const [theResource, theAttribute] = role.split('.');
            if (theResource && theAttribute) {
              thePermissions[theResource] = thePermissions[theResource] || {};
              thePermissions[theResource][theAttribute.toLowerCase()] = roles.includes(role);
            }
          }
        });
      }

      return (hasAccess && hasRoles && thePermissions?.[resource]?.[attribute]) || false;
    },
    [hasAccess, currentRole, permissions, hasRoles, roles]
  );

  /**
   * User
   *
   * Aggregates all user context data.
   *
   * @type {DefaultValues}
   */
  const user = useMemo(() => {
    return merge({}, defaultValues, {
      ...account,
      currentRole,
      hasAccess,
      hasPermission,
      hasRoles,
      roles,
    });
  }, [account, roles, hasAccess, currentRole, hasRoles, hasPermission]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}
